import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "./components/Sidebar";
import API_BASE_URL from "./config";
import "./Team.css";
import { clearAuthStorage } from "./auth/logout";

const PERMISSIONS = [
  { value: "view_only", label: "View only", desc: "Browse and download photos. No uploads, no edits." },
  { value: "upload_only", label: "Upload only", desc: "Create events and upload photos. Can't edit or delete." },
  { value: "editor", label: "Editor", desc: "Upload, edit event details, and delete events/photos." },
];

const PERMISSION_META = Object.fromEntries(PERMISSIONS.map((p) => [p.value, p]));

function Team() {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);
  const [members, setMembers] = useState([]);
  const [maxTeamMembers, setMaxTeamMembers] = useState(0);
  const [activeCount, setActiveCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", title: "", permission: "upload_only" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loadError, setLoadError] = useState("");

  const token = localStorage.getItem("token");
  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const loadMembers = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const res = await fetch(`${API_BASE_URL}/api/team/members`, { headers });
      if (res.status === 401) {
        // Stale/expired session — send them back to log in rather than showing a
        // misleading "not on your plan" state (maxTeamMembers defaults to 0 unset).
        clearAuthStorage();
        navigate("/login");
        return;
      }
      const data = await res.json();
      if (res.ok) {
        setMembers(data.members || []);
        // Preserve null — the API sends null for plans with unlimited team seats
        // (Pro Max / legacy Pro Plus). Coercing it to 0 here would read as "not on
        // your plan" and wrongly show the upgrade lock to a paying subscriber.
        setMaxTeamMembers(data.maxTeamMembers === undefined ? 0 : data.maxTeamMembers);
        setActiveCount(data.activeCount ?? 0);
      } else {
        setLoadError(data.message || "Failed to load your team");
      }
    } catch (e) {
      setLoadError("Failed to load your team. Check your connection and try again.");
    }
    setLoading(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadMembers(); }, [loadMembers]);

  const seatsAvailable = maxTeamMembers == null || maxTeamMembers === "" ? true : activeCount < maxTeamMembers;
  const planIncludesTeam = maxTeamMembers == null || maxTeamMembers > 0;

  const handleFormChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/team/members`, {
        method: "POST",
        headers,
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to add team member");
      setSuccess(`${form.title || "Team member"} added!`);
      setForm({ email: "", password: "", title: "", permission: "upload_only" });
      setShowAddForm(false);
      await loadMembers();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (member) => {
    const nextStatus = member.status === "active" ? "suspended" : "active";
    setError("");
    try {
      const res = await fetch(`${API_BASE_URL}/api/team/members/${member.id}`, {
        method: "PATCH",
        headers,
        body: JSON.stringify({ status: nextStatus }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to update team member");
      await loadMembers();
    } catch (err) {
      setError(err.message);
    }
  };

  const handlePermissionChange = async (member, permission) => {
    setError("");
    try {
      const res = await fetch(`${API_BASE_URL}/api/team/members/${member.id}`, {
        method: "PATCH",
        headers,
        body: JSON.stringify({ permission }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to update permission");
      await loadMembers();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRemove = async (member) => {
    if (!window.confirm(`Remove ${member.member_title || member.email}? They'll lose access immediately.`)) return;
    setError("");
    try {
      const res = await fetch(`${API_BASE_URL}/api/team/members/${member.id}`, {
        method: "DELETE",
        headers,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to remove team member");
      await loadMembers();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="team-container">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="team-main">
        <header className="team-header">
          <div className="team-header-left">
            <button className="team-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>☰</button>
            <h2>🧑‍🤝‍🧑 Team</h2>
          </div>
          {!loading && !loadError && planIncludesTeam && (
            <button
              className="team-add-btn"
              onClick={() => { setShowAddForm(true); setError(""); setSuccess(""); }}
              disabled={!seatsAvailable}
            >
              + Add Staff
            </button>
          )}
        </header>

        <div className="team-content">
          {loading ? (
            <div className="team-loading">Loading...</div>
          ) : loadError ? (
            <div className="team-empty">
              <div className="team-empty-icon">⚠️</div>
              <h3>Couldn't load your team</h3>
              <p>{loadError}</p>
              <button className="team-empty-btn" onClick={loadMembers}>Try Again</button>
            </div>
          ) : !planIncludesTeam ? (
            <div className="team-empty">
              <div className="team-empty-icon">🔒</div>
              <h3>Team accounts are a Pro / Pro Max feature</h3>
              <p>Add second-shooter and editor logins for your studio — upgrade to start inviting staff.</p>
              <button className="team-empty-btn" onClick={() => navigate("/wallet")}>
                Upgrade Plan
              </button>
            </div>
          ) : (
            <>
              <div className="team-seats">
                <span>
                  {activeCount} / {maxTeamMembers == null ? "∞" : maxTeamMembers} seats used
                </span>
                {!seatsAvailable && <span className="team-seats-full">All seats in use — suspend a member or upgrade for more.</span>}
              </div>

              {error && <div className="team-banner team-banner-error">{error}</div>}
              {success && <div className="team-banner team-banner-success">{success}</div>}

              {showAddForm && (
                <form className="team-add-form" onSubmit={handleAddMember}>
                  <div className="team-form-row">
                    <input
                      type="text" name="title" placeholder="Role, e.g. Photographer, Editor"
                      value={form.title} onChange={handleFormChange}
                    />
                    <select name="permission" value={form.permission} onChange={handleFormChange}>
                      {PERMISSIONS.map((p) => (
                        <option key={p.value} value={p.value}>{p.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="team-form-row">
                    <input
                      type="email" name="email" placeholder="Email" required
                      value={form.email} onChange={handleFormChange}
                    />
                    <input
                      type="password" name="password" placeholder="Password (min 6 characters)" required minLength={6}
                      value={form.password} onChange={handleFormChange}
                    />
                  </div>
                  <p className="team-form-hint">{PERMISSION_META[form.permission]?.desc}</p>
                  <p className="team-form-note">
                    💡 Your staff member can sign in with this password, or use "Continue with Google" —
                    as long as it's the same email, either way logs them in with this exact access.
                  </p>
                  <div className="team-form-actions">
                    <button type="button" className="team-cancel-btn" onClick={() => setShowAddForm(false)}>Cancel</button>
                    <button type="submit" className="team-save-btn" disabled={saving}>
                      {saving ? "Adding..." : "Add Staff"}
                    </button>
                  </div>
                </form>
              )}

              {members.length === 0 ? (
                <div className="team-empty">
                  <div className="team-empty-icon">🧑‍🤝‍🧑</div>
                  <h3>No staff logins yet</h3>
                  <p>Add a second shooter or editor with their own login and scoped permissions.</p>
                </div>
              ) : (
                <div className="team-grid">
                  {members.map((m) => (
                    <div key={m.id} className={`team-card ${m.status === "suspended" ? "suspended" : ""}`}>
                      <div className="team-card-top">
                        <h3 className="team-card-title">{m.member_title || "Staff"}</h3>
                        <span className={`team-card-status status-${m.status}`}>{m.status}</span>
                      </div>
                      <div className="team-card-email">{m.email}</div>
                      <select
                        className="team-card-permission"
                        value={m.member_permission}
                        onChange={(e) => handlePermissionChange(m, e.target.value)}
                      >
                        {PERMISSIONS.map((p) => (
                          <option key={p.value} value={p.value}>{p.label}</option>
                        ))}
                      </select>
                      <p className="team-card-desc">{PERMISSION_META[m.member_permission]?.desc}</p>
                      <div className="team-card-actions">
                        <button className="team-card-btn" onClick={() => handleToggleStatus(m)}>
                          {m.status === "active" ? "Suspend" : "Reactivate"}
                        </button>
                        <button className="team-card-btn team-card-btn-danger" onClick={() => handleRemove(m)}>
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default Team;
